/**
 * /guides/what-to-include-digital-wedding-invitation
 *
 * "What to Include in a Digital Wedding Invitation" — the thirteenth guide, and
 * the second under the invitation product. Guide twelve answered which KIND of
 * thing a couple should have (a website, an invitation, or both); this one
 * answers what goes inside the invitation once they have chosen it, and where
 * inside it each piece belongs.
 *
 * ── What the SERP contains, and the gap ───────────────────────────────────
 * The query is well served in one narrow sense and badly served in another.
 * Every major stationery and planning site has a "what to include in wedding
 * invitations" checklist, and they agree closely: names, date, ceremony time,
 * venue, reception line, RSVP and its deadline, plus a details card for
 * everything else. Two things are missing from all of them:
 *
 *   1. They are written for a PRINTED SUITE. The structure they describe is a
 *      physical one — an invitation card, an RSVP card, a details card, an
 *      envelope whose addressing carries the plus-one policy. A couple sending
 *      one link has none of those containers, and the advice does not translate
 *      cleanly. "Put it on the details card" is not an answer when there is no
 *      card.
 *   2. They are completeness checklists. Every one of them adds items; none of
 *      them says what to leave out, or what to demote. On paper the cost of
 *      adding a line is money and space, which disciplines the list by itself.
 *      In a digital invitation adding a line is free, so the discipline has to
 *      come from editorial judgement — and that is precisely where the market's
 *      advice stops.
 *
 * So the differentiator is the three levels — essential, helpful, situational —
 * applied to a format where everything CAN fit and therefore something has to
 * decide what should. The article's own thesis, stated in the intro and again
 * at the close: more room to organise information is not more reason to include
 * it.
 *
 * ── The rule the article is written under ─────────────────────────────────
 * NO ETIQUETTE LAW. The printed-invitation literature is heavy with rules
 * ("the honour of your presence" for a religious ceremony, spell the year out,
 * the dress code sits bottom right) that are conventions of one tradition and
 * are not true of a Greek wedding, a civil ceremony or a second marriage. Every
 * recommendation here is therefore conditional — "usually", "for most
 * weddings", "if relevant", "when guests need it" — and the three-level model
 * itself is introduced as a way to decide rather than as a specification. Two
 * places where the temptation to legislate is strongest, and where the article
 * deliberately does not:
 *
 *   - hosts and host lines: a family-structure and tradition question, not a
 *     formatting one. The article says name the hosts where it matters to the
 *     couple, and stops.
 *   - children and plus-ones: the printed convention (encode it in the envelope
 *     addressing) does not exist digitally, and no policy is presented as
 *     correct. What the article asks for is that a guest can TELL, which is the
 *     part that is genuinely universal.
 *
 * ── The claims this guide is allowed to make about Agapaé ─────────────────
 * Verified in the same places guide twelve verified them, and re-checked for
 * this article: `api/constants/planFeatures.mjs` (invitationProduct is
 * `pro`/Heirloom only), the `GET /api/events/:eventId/public` projection in
 * `api/routes/events.routes.mjs` (exactly which fields the invitation can
 * carry), `invite/src/pages/RSVP.jsx` (which sections render) and
 * `invite/src/components/rsvp/useRsvpFlow.js` (replies are submitted from
 * inside the invitation, per member and per section).
 *
 * What the payload actually carries, and therefore what may be claimed: the
 * couple's names, the date, the church and the venue with their map links, the
 * schedule of the day (each entry a time, a title and an optional line of
 * description), the couple's story, photographs, gift information (a note, bank
 * details, or a link to a registry the couple already has), the couple's phone
 * numbers shown with the RSVP, the RSVP deadline, and the RSVP itself.
 *
 * What must NOT be claimed, and why each one is a live risk on THIS article in
 * a way it was not on the last one — the guide recommends things the product
 * has no field for, so the Agapaé section has to read as a list of what is
 * there rather than as an answer to the checklist above it:
 *
 *   - NO dress-code field. The article recommends a dress code; the invitation
 *     has no dedicated place for one. Do not imply otherwise.
 *   - NO travel, accommodation, parking, accessibility or FAQ sections.
 *   - NO registry hosting, gift list or payment processing. `giftRegistryUrl`
 *     is a link to a registry kept elsewhere, and `giftIban` is a bank detail
 *     the couple types in. The product stores and displays them; it does not
 *     run them.
 *   - NOT a wedding website builder, and NO template gallery: one editorial
 *     design, in Greek and English.
 *   - NO custom domain or chosen URL. The link is an auto-generated event id
 *     (`api/utils/generators.mjs`), so "its own private link" is the ceiling.
 *   - NO "unlimited", "permanent" or "forever" anywhere.
 *
 * The honest sentence the section is built around — and it is also the article's
 * own advice — is that the invitation holds the essentials and the details that
 * belong with them, and that anything it has no place for belongs wherever the
 * couple tells guests the rest.
 *
 * ── Structure ─────────────────────────────────────────────────────────────
 * Sixteen H2s: fourteen editorial sections, the Agapaé section, and the closing
 * principle. The commission listed fifteen subjects; the only merge is that
 * "what belongs on the main invitation versus deeper" is not a section of its
 * own — it is the three-level model introduced in section one, applied in every
 * section after it, and restated as the checklist. Giving it a section would
 * mean stating the article's thesis three times instead of twice.
 *
 * The Agapaé section sits second to last rather than last, and the article
 * closes on the principle, exactly as guide twelve does: the `related` block
 * below already renders "Where this fits in Agapaé" as an H2, and ending the
 * prose on a product claim would put two Agapaé headings in a row.
 *
 * ── One thing to leave alone ──────────────────────────────────────────────
 * No copy in this file may contain "&", "<" or ">". The generator escapes them
 * correctly, but `guides.seo.test.mjs` compares against tag-stripped HTML
 * without decoding entities, so a string containing one is reported missing
 * from a document that in fact carries it.
 */

const whatToIncludeDigitalWeddingInvitationEn = {
  slug: "what-to-include-digital-wedding-invitation",

  title: "What to Include in a Digital Wedding Invitation",
  metaTitle: "What to Include in a Digital Wedding Invitation | Agapaé",
  metaDescription:
    "Learn what to include in a digital wedding invitation, from names and wedding details to RSVP, venue information, dress code, and helpful guest details.",

  eyebrow: "Digital invitations",

  hero: {
    src: "/assets/images/lovely_couple_004.jpeg",
    // Describes the photograph, not the keyword: a black and white portrait of
    // a bride at night, seen in profile through a sheer veil, with small blurred
    // lights scattered in the darkness behind her.
    alt: "A black and white portrait of a bride in profile, her face seen through a sheer veil, with small blurred lights in the darkness behind her",
    width: 1280,
    height: 714,
  },

  // Absent until the guide is genuinely published. Adding one means adding the
  // other, and `dateModified` may not precede `datePublished`.
  // datePublished: "YYYY-MM-DD",
  // dateModified: "YYYY-MM-DD",

  intro: [
    `Your guests will open the invitation on a phone, once, probably while they are in the middle of something else.`,
    `In that moment they are looking for a small number of things: whose wedding this is, when it is, where it is, and what you are asking them to do about it. Everything else can wait. Those cannot.`,
    `A digital invitation gives you far more room than a printed card ever did. That is genuinely useful, and it is also the trap: more room to organise information is not more reason to include it. An invitation that says everything at once says nothing clearly.`,
    `This guide sets out what usually belongs on the invitation itself, what sits comfortably a level deeper, and what is only worth including for certain celebrations.`,
  ],

  sections: [
    {
      heading: "What Should a Digital Wedding Invitation Include?",
      content: [
        `Read your own invitation as a guest rather than its author, and it must answer five questions, in this order:`,
        {
          type: "list",
          items: [
            `who is getting married`,
            `when the wedding is`,
            `where it takes place`,
            `how to reply, and by when`,
            `anything else a guest needs in order to come`,
          ],
        },
        `The first four are the invitation. The fifth is where couples differ, and where digital invitations either become genuinely useful or become overwhelming.`,
        `A helpful way to hold it is three levels of information rather than one long list.`,
        `Essential is what a guest should find immediately, without scrolling or searching: the names, the date, the ceremony time, the place, and how to respond. Helpful is what makes coming easier: the shape of the day, a map link, the dress code, how to arrive, who to ask. Situational matters for some weddings and not others: travel, accommodation, several events, who exactly is invited, gifts.`,
        `Nothing in the second or third level is unimportant. It simply does not need to compete with the first.`,
        {
          type: "note",
          text: `The levels shift with the celebration. If your ceremony and reception are in different towns, the reception is not a helpful detail. It is essential.`,
        },
      ],
    },
    {
      heading: "Start With the Names",
      content: [
        `An invitation is from someone, to someone, about a particular day. The names establish all three, so they belong at the top and they should be immediately legible.`,
        `Whether you use full names or first names is a question of tone rather than of correctness. Full names read formally and are unambiguous; first names read warmly and work well when everyone invited already knows you both. Both are perfectly ordinary.`,
        `Where the people hosting the wedding matter to you or to your families, name them. Some traditions place the hosts above the couple, some name both families, and many couples now invite in their own names. It is a family question rather than a formatting rule, with no version that is right for everyone.`,
        `One consideration is particular to digital invitations: they get forwarded. A link arriving second-hand, weeks later, carries none of the context an envelope did. Names that identify the wedding on their own are worth more here than they ever were on paper.`,
      ],
    },
    {
      heading: "Make the Date and Time Unmistakable",
      content: [
        `The date is the piece guests act on first, because everything else in their life has to move around it.`,
        `Include the full date, with the year. On a printed invitation the year is often treated as optional; in a digital one it is not, because the invitation lives in a message thread that will be scrolled past for months and reopened at some odd moment. The day of the week is a small courtesy that saves everyone a look at a calendar.`,
        `Then the time the ceremony begins. This is the time guests plan against, so it should be stated plainly rather than implied by a schedule further down. If you would like people seated before that, say both: when to arrive, and when it starts.`,
        `Where the reception begins separately, or hours later, that time matters too. A gap between the ceremony and the reception is one of the things guests most need to know in advance, and one of the most commonly left out.`,
      ],
    },
    {
      heading: "Give Guests a Clear Sense of Place",
      content: [
        `Location is where invitations lose people most often, usually because the couple knows the venue so well that they forget how little its name conveys.`,
        `For most weddings the useful set is the venue name, the town or area, and the full address wherever the venue is not obvious from its name alone. Rural venues, private estates and anywhere sharing a name with somewhere else all need the address.`,
        `If the ceremony and the reception are in different places, say so clearly and give both. Guests plan their transport around this, and a single address for a two-location day is the most consequential omission on this list.`,
        `A map link is a real improvement on paper, and worth including. It is not a replacement for written location detail, though: a link tells a guest nothing while they are still deciding whether they can get there at all. Give the place in words, then the link that takes them to it.`,
      ],
    },
    {
      heading: "Explain What Happens Next",
      content: [
        `Guests accepting an invitation are quietly working out what they are agreeing to. Is there dinner. Does this run late. How long will we need someone with the children. Is the ceremony the whole of it, or the beginning of it.`,
        `A short sense of the sequence answers all of that in a line or two. A ceremony and then a reception in the same place; a ceremony, then dinner elsewhere; a celebration that carries on into the night. For most weddings that is enough.`,
        `What guests rarely need is the full timeline of the day. The order of the speeches, the first dance and the cake are yours to run, and publishing them turns the invitation into a schedule of a plan that will change anyway. Where the day genuinely has parts guests must move between, a simple list of the moments that involve them is the right amount of detail.`,
      ],
    },
    {
      heading: "Make the RSVP Simple",
      content: [
        `The reply is the one thing an invitation asks a guest to do, so it should be the most obvious action on the page.`,
        {
          type: "p",
          text: [
            `That means three things stated together: that you would like a reply, how to give it, and by when. A `,
            { text: "wedding RSVP", href: "/wedding-rsvp" },
            ` that lives inside the invitation is the simplest version of this, because reading and replying happen in one place and nobody has to remember an address or find an email later.`,
          ],
        },
        `Not every digital invitation can collect replies. One sent as an image, a PDF or a video cannot take anything, so the reply has to happen somewhere else. If that is your format, name the destination explicitly rather than assuming guests will work it out.`,
        {
          type: "p",
          text: [
            `Put the deadline beside the instruction rather than at the foot of the page. A deadline that is only visible after scrolling is a deadline half your guests will not see, and chasing replies afterwards costs far more than the line would have. For how far ahead of the wedding to set it, see `,
            {
              text: "when wedding guests should RSVP",
              href: "/guides/when-should-wedding-guests-rsvp",
            },
            `.`,
          ],
        },
      ],
    },
    {
      heading: "The Details Guests Will Actually Need",
      content: [
        `Below the essentials sits the practical layer: the things that do not change whether someone comes, but do change how easily they arrive and how comfortable they feel.`,
        `The test for each one is relevance rather than completeness: include it if a guest would otherwise have to ask, and leave it out if the answer is obvious.`,
        { type: "subheading", text: "Dress code" },
        `Worth including for most weddings, and usually in two or three words. Guests ask this question anyway, and they ask it privately, of whichever member of your family they are least embarrassed to ask. Where something about the celebration genuinely affects what people should wear, such as a beach, a long walk or a church with expectations of its own, one clarifying sentence is fair.`,
        { type: "subheading", text: "Arriving" },
        `Parking, transport and access matter when there is something to say: limited parking, a venue that is hard to reach, a coach you are arranging, a gate that closes. Where arriving is straightforward, explaining it at length only implies that it is not.`,
        { type: "subheading", text: "Someone to ask" },
        `A name and a way to reach someone resolves the questions no invitation can anticipate. For many couples that person is not the couple themselves: someone who is not getting married that week is often the better contact.`,
      ],
    },
    {
      heading: "When Travel and Accommodation Belong in the Invitation",
      content: [
        `This is the clearest case of situational information, and the place where invitations most often turn into something else.`,
        `It becomes important when guests are booking flights or rooms rather than driving over for the afternoon: a destination wedding, a rural venue with little nearby, a date that falls in a local season when rooms disappear. Those guests are making decisions with deadlines of their own, and the earlier they know the better.`,
        `It stays out when it does not apply. A local wedding where a handful of guests might stay over does not need an accommodation section. Those guests need a message.`,
        {
          type: "p",
          text: [
            `Where there genuinely is a great deal of it, the honest answer is usually not to put it all in the invitation. Keep the invitation to the invitation and point onward to wherever the detail lives, whether that is a wedding website, a shared document or a note to the people affected. That choice is what `,
            {
              text: "a wedding website and a digital invitation",
              href: "/guides/wedding-website-vs-digital-wedding-invitation",
            },
            ` really divide between them.`,
          ],
        },
      ],
    },
    {
      heading: "Plus-Ones, Children, and Who the Invitation Covers",
      content: [
        `Every guest reads an invitation with one private question in mind: does this include the people I would normally bring.`,
        `Printed invitations answered it through the envelope, by naming exactly who was addressed. A link has no envelope, so if you leave the question unanswered your guests will either ask or assume, and both are worse than being told.`,
        `Digital invitations have a real advantage here, which is that each household can have its own. When the invitation names the people it is for, and the reply is for those same people, the question resolves itself without any delicate wording at all.`,
        `Where you do need to say something, say it once, warmly, and in ordinary language. Whether children are coming, whether guests may bring someone, and how many seats you have held are your decisions rather than matters of etiquette. Two things are worth holding to: consistency, so that the same rule applies to everyone outside your own family, and clarity, so that nobody arrives with a person you had not counted.`,
      ],
    },
    {
      heading: "Gifts, Handled Lightly",
      content: [
        `Gift information is optional, and how it is handled varies enormously by country, by family and by generation. In some places a bank detail on the invitation is entirely ordinary; in others a registry link is the norm; in others nothing is said at all and everyone understands what is meant.`,
        `If you include something, the useful version is short and secondary: a sentence in your own voice, and whichever practical detail follows from it, placed below the parts of the invitation that bear on attendance.`,
        `What guests notice is prominence rather than presence. Gift information near the top of an invitation reads differently from the same words further down, whatever they say. Almost nobody minds a line they found while looking for something else.`,
      ],
    },
    {
      heading: "When a Celebration Has More Than One Moment",
      content: [
        `Many weddings are not a single event. There may be a welcome gathering the evening before, a ceremony, a reception, and something quieter the next day, and the guest lists for those are rarely identical.`,
        `A digital invitation handles this better than a card does, because it can hold several moments without a separate insert for each. What it should not do is give them equal weight. The wedding leads; everything else follows it, clearly marked, with its own time and place attached.`,
        {
          type: "p",
          text: [
            `The part that matters most is the reply. When guests are invited to more than one moment a single yes tells you very little, and knowing who is coming to the dinner is not the same as knowing who is coming to the ceremony. An invitation that takes attendance moment by moment gives you numbers you can actually plan from, which is where `,
            {
              text: "organising wedding RSVPs",
              href: "/guides/how-to-organize-wedding-rsvps",
            },
            ` begins.`,
          ],
        },
      ],
    },
    {
      heading: "Keep the Opening Simple",
      content: [
        `There is a difference between the invitation a guest first sees and the complete set of information you are giving them, and a digital invitation is the format where couples most often collapse the two into one.`,
        `The first screen is doing a single job: making it unmistakable whose wedding this is, when, where, and that they are invited to it. Names, date, place, and the feeling of the celebration. Nothing there should have to be studied.`,
        `Everything else follows below it, or one level deeper, in the order guests would look for it. The reply near the top of what follows, because it is the thing you are asking for. The practical details after that. The situational information last, where the people it applies to will find it and everyone else will pass it by.`,
        `This is the real advantage of the format. A printed suite solved the same problem with separate cards; a digital invitation solves it with order and depth, and one that opens with everything at once has simply chosen not to.`,
      ],
    },
    {
      heading: "A Simple Information Checklist",
      content: [
        `A short version to check a draft against, rather than a list to fill in.`,
        { type: "subheading", text: "Essential" },
        {
          type: "list",
          items: [
            `the couple's names`,
            `the date, with the year`,
            `the ceremony time`,
            `the venue and its location`,
            `how to reply`,
            `the RSVP deadline`,
          ],
        },
        { type: "subheading", text: "Helpful" },
        {
          type: "list",
          items: [
            `reception details, and the shape of the day`,
            `a map link beside the written location`,
            `the dress code`,
            `what guests need in order to arrive`,
            `someone to contact with a question`,
          ],
        },
        { type: "subheading", text: "When relevant" },
        {
          type: "list",
          items: [
            `accommodation and travel`,
            `other events around the wedding`,
            `who the invitation covers, where that is not obvious`,
            `gift information`,
          ],
        },
      ],
    },
    {
      heading: "Before You Send: Check the Guest Experience",
      content: [
        `A digital invitation can be corrected after it has gone out, which is a real advantage over print. It is a better advantage if you never need it.`,
        `Open your own invitation on a phone, from the message a guest will receive, and read it as one of them:`,
        {
          type: "steps",
          items: [
            `Are all the names spelled the way their owners spell them.`,
            `Is the date unambiguous, with the year, and is the ceremony time the one you mean.`,
            `Could someone who has never been to the venue find it from what is written.`,
            `Is it obvious what you are asking guests to do, and where.`,
            `Is the deadline visible without scrolling to look for it.`,
            `Does every link open, the map and the reply included.`,
            `Does it read well on a small screen, in one hand, in daylight.`,
          ],
        },
        `Then send it to two people who are not you: someone close to the wedding who knows the details, and someone who knows nothing about it. The first catches what is wrong; the second catches what is missing, which is harder to see and matters more.`,
      ],
    },
    {
      heading: "One Invitation, Organised for Guests",
      content: [
        {
          type: "note",
          text: `Agapaé's digital wedding invitation is available on the Heirloom plan.`,
        },
        {
          type: "p",
          text: [
            `Your `,
            {
              text: "digital wedding invitation",
              href: "/digital-wedding-invitation",
            },
            ` is one editorial page at its own private link, arranged in roughly the order this guide describes: your names and your date first, then your story and your photographs, the church and the venue with their map links, the schedule of the day, gift information if you want it, and the reply at the end.`,
          ],
        },
        `Guests reply inside the invitation, for themselves and for the people with them, and moment by moment where the celebration has more than one. Those answers become your guest list rather than a set of messages to reconcile afterwards.`,
        `It is worth being precise about what the invitation is not. There is no travel section, no accommodation list and no dedicated field for a dress code: Agapaé is an invitation rather than a wedding website builder, one editorial design in Greek and English rather than a site to assemble page by page. Anything it has no place for belongs wherever you tell your guests the rest, which for most weddings is a short message to the people it affects.`,
      ],
    },
    {
      heading: "What Makes an Invitation Feel Complete",
      content: [
        `A complete invitation is not the one with the most information. It is the one that gives guests the right information at the moment they need it, and keeps the rest close enough to find.`,
        `Answer who, when, where and how to reply without making anyone look for it. Put the details that help below that, and the ones that apply only to some guests below those. Then read it once more on a phone, as someone who was not there when you chose the venue.`,
      ],
    },
  ],

  faqHeading: "Frequently Asked Questions",

  // Visible copy, always expanded. `faqSchema` is deliberately not set, exactly
  // as on the twelve earlier guides: Google restricts FAQ rich results to
  // government and health sites, so the markup would buy nothing while
  // committing us to a second copy of every answer.
  faq: [
    {
      q: `What information is essential on a digital wedding invitation?`,
      a: `For most weddings: the couple's names, the date with the year, the time the ceremony begins, the venue and its location, how to reply, and the RSVP deadline. Anything beyond those six is worth including when it helps guests attend, and worth moving further down when it competes with them for attention.`,
    },
    {
      q: `Should a digital wedding invitation include an RSVP?`,
      a: `Where the format allows one, yes. Replying in the same place the invitation is read is simpler for guests and gives you cleaner answers. An invitation sent as an image, a PDF or a video cannot collect a reply, so name where the reply should go instead, and state the deadline beside it.`,
    },
    {
      q: `Should I include the dress code on the invitation?`,
      a: `Usually, and briefly. Two or three words answer a question most guests would otherwise ask someone privately. Add a clarifying sentence only where something about the celebration genuinely affects what people should wear, such as a beach, a long walk or a church with expectations of its own.`,
    },
    {
      q: `Should I include accommodation and travel information?`,
      a: `Only when guests need it. Include it when people are booking flights or rooms, as at a destination wedding or a rural venue with little nearby. Where there is a great deal of it, keep the invitation focused and point onward to wherever the detail lives rather than turning the invitation into a travel guide.`,
    },
    {
      q: `Can a digital wedding invitation replace a wedding website?`,
      a: `Often, for a wedding in one place with a straightforward journey. An invitation carrying the venue, the shape of the day, map links and the reply already covers what many couples would otherwise build a site for. What it does not usually replace is a detailed travel and accommodation guide, or a long list of questions and answers.`,
    },
  ],

  related: {
    heading: "Where this fits in Agapaé",

    // Three: the guide this one is the sequel to, and the two RSVP guides the
    // reply sections hand off to. Each is already linked once in the prose at
    // the point a reader needs it, so every destination here sits at two —
    // well under the cap the test enforces.
    guides: [
      "wedding-website-vs-digital-wedding-invitation",
      "when-should-wedding-guests-rsvp",
      "how-to-organize-wedding-rsvps",
    ],

    product: [
      {
        href: "/digital-wedding-invitation",
        label: "Digital wedding invitation",
        blurb:
          "See the invitation itself: one editorial design at its own private link, carrying your names, your venue with its map links, the schedule of the day and the reply. Available on the Heirloom plan.",
      },
      {
        href: "/wedding-rsvp",
        label: "Wedding RSVP",
        blurb:
          "See how guests reply from inside the invitation, for each person with them and each moment of the celebration, and how those answers become the guest list you plan from.",
      },
    ],
  },
};

export default whatToIncludeDigitalWeddingInvitationEn;
